import { motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';

import { useTranslation } from 'react-i18next';
import { ScreenWithFields, useDynamicForm } from './DynamicFormContext';

interface ValidationErrorBannerProps {
  currentScreen: ScreenWithFields;
}

export default function ValidationErrorBanner({ currentScreen }: ValidationErrorBannerProps) {
  const { t } = useTranslation();
  const { errors, getVisibleFields } = useDynamicForm();

  if (Object.keys(errors).length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="mb-6 rounded-xl border-2 mx-4 border-amber-300 bg-linear-to-r from-amber-50 to-amber-100/50 p-4 md:p-5 shadow-md shadow-amber-200/20"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-400">
          <AlertTriangle className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 space-y-2">
          <h3 className="font-semibold text-lg text-amber-800">
            {t('EMPLOYMENTAGREEMENT_SMARTFLOW_VALIDATION_TITLE')}
          </h3>
          <p className="text-sm text-amber-700">
            {t('EMPLOYMENTAGREEMENT_SMARTFLOW_VALIDATION_DESCRIPTION')}
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm text-amber-800">
            {Object.entries(errors).map(([fieldName, errorMessage]) => {
              const field = getVisibleFields(currentScreen).find((f) => f.name === fieldName);
              return (
                <li key={fieldName} className="text-amber-800">
                  <strong>{field?.label || fieldName}</strong>: {errorMessage}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </motion.div>
  );
}
